import {
  BandwidthDisplayType,
  FixedLineAccessTechnologyType,
  OfferType,
  ProductType,
  TariffCatalogType,
  TariffOfferType,
} from '@/types/av-check';
import { groupBy } from 'lodash';
import {
  getTariffCatalogComparator, getTariffOfferComparator,
  isAlternateTariffOffer,
} from '@/utils/offers';

const groupIdentifierKey = (tariffOffer: TariffOfferType) => {
  if (tariffOffer.externalId) {
    return isAlternateTariffOffer(tariffOffer) ? 'alternateTo' : 'externalId';
  }
  return 'tariffVariationCode';
};

const groupIdentifierValue = (tariffOffer: TariffOfferType) => tariffOffer[groupIdentifierKey(tariffOffer)] || tariffOffer.technicalRank.toString();

const toTariffCatalog = (tariffOffers: TariffOfferType[], selectedBandwidth: BandwidthDisplayType | undefined) => ({
  dslTariffOffer: tariffOffers.find(
    (tariff) => ((tariff.type === ProductType.DSL) && (tariff.accessTechnology === FixedLineAccessTechnologyType.DSL)
          && (selectedBandwidth ? (tariff.offerType) : true)),
  ),
  coaxTariffOffer: tariffOffers.find(
    (tariff) => ((tariff.type === ProductType.DSL) && (tariff.accessTechnology === FixedLineAccessTechnologyType.COAX)
          && (selectedBandwidth ? (tariff.offerType) : true)),
  ),
  fmsTariffOffer: tariffOffers.find(
    (tariff) => ((tariff.type === ProductType.FMS) && (tariff.accessTechnology === FixedLineAccessTechnologyType.FMS)
          && (selectedBandwidth ? (tariff.offerType) : true)),
  ),
  ftthTariffOffer: tariffOffers.find(
    (tariff) => ((tariff.type === ProductType.DSL) && (tariff.accessTechnology === FixedLineAccessTechnologyType.FTTH)
          && (selectedBandwidth ? (tariff.offerType) : true)),
  ),
});

function notEmptyTariffCatalog(tariffCatalog: TariffCatalogType) {
  return tariffCatalog.coaxTariffOffer || tariffCatalog.dslTariffOffer || tariffCatalog.fmsTariffOffer || tariffCatalog.ftthTariffOffer;
}

export function prepareTechnologyAgnosticGrouping(tariffOffers: TariffOfferType[], selectedBandwidth: BandwidthDisplayType): TariffOfferType[] {
  let selectedBandwidthIndex: number | undefined;
  let upsellGroupIdentifier: any | undefined;
  let upsellIndex: number | undefined;
  return tariffOffers
    .sort(getTariffOfferComparator())
    .map((item, index) => {
      if (selectedBandwidthIndex === undefined && item.cmsTariffDescription.downloadBandwidthText.content === selectedBandwidth.text) {
        selectedBandwidthIndex = index;
        return Object.assign(item, { offerType: OfferType.SELECTED });
      }
      return item;
    })
    .map((item, index) => {
      if (selectedBandwidthIndex && index === selectedBandwidthIndex - 1) {
        upsellGroupIdentifier = groupIdentifierValue(item);
      }
      return item;
    })
    .map((item, index) => {
      if (selectedBandwidthIndex && upsellIndex === undefined && upsellGroupIdentifier === groupIdentifierValue(item)) {
        upsellIndex = index;
        return Object.assign(item, { offerType: OfferType.UPSELL });
      }
      if (selectedBandwidthIndex === undefined && index === 0) {
        return Object.assign(item, { offerType: OfferType.DOWNSELL });
      }
      return item;
    });
}

export const groupByMainAlternate = (tariffOffers: TariffOfferType[], selectedBandwidth: BandwidthDisplayType | undefined) => {
  const preparedTariffOffers = selectedBandwidth ? prepareTechnologyAgnosticGrouping(tariffOffers, selectedBandwidth) : tariffOffers;
  return Object.values(groupBy(
    preparedTariffOffers.map((tariffOffer) => ({
      groupId: groupIdentifierValue(tariffOffer),
      ...tariffOffer,
    })), 'groupId',
  )).map((tariffWithGroupId) => toTariffCatalog(tariffWithGroupId, selectedBandwidth))
    .filter((tariffGroup) => notEmptyTariffCatalog(tariffGroup))
    .sort(getTariffCatalogComparator());
};
